import { useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { RouteGuardPermissions } from "@/components/permissions/route-guard-multiple";
import DebitNotesForm from "@/components/invoices/debits/debit-notes-form";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute(
  "/_authenticated/invoices/debit-notes/new",
)({
  validateSearch: (search: Record<string, unknown>) => ({
    draft: typeof search.draft === "string" ? search.draft : undefined,
  }),
  component: NewDebitNotePage,
});

function NewDebitNotePage() {
  const { draft } = Route.useSearch();
  const coldDraftRef = useRef(draft);

  return (
    <RouteGuardPermissions
      permissions={[{ module: "debit_notes", actions: ["create"] }]}
    >
      {coldDraftRef.current ? (
        <DebitNoteHydrator
          debitNoteId={coldDraftRef.current as Id<"debit_notes">}
        />
      ) : (
        <DebitNotesForm />
      )}
    </RouteGuardPermissions>
  );
}

function DebitNoteHydrator({ debitNoteId }: { debitNoteId: Id<"debit_notes"> }) {
  const debitNote = useQuery(
    api.companies.debit_notes.queries.getDebitNoteById,
    { debitNoteId },
  );

  if (debitNote === undefined) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!debitNote || debitNote.status !== "draft") {
    return <DebitNotesForm />;
  }

  return <DebitNotesForm debitNote={debitNote as never} />;
}
